import { useState } from 'react'

import { cn } from '@/lib/utils'

import { StarIcon } from './StarIcon'
import { useStarsVariants } from '../../Theme'

interface StarRatingInputProps {
  /** Tailwind size classes of one star (e.g., 'h-8 w-8') */
  starSize: string
  /** Callback when a star is selected, with the rating (1-5) */
  onStarSelect?: (rating: number) => void
  /** Additional container classes */
  className?: string
}

/**
 * StarRatingInput - Click-to-rate input
 *
 * Starts with no star selected. Each star is a button, for mouse and keyboard.
 */
export function StarRatingInput({ starSize, onStarSelect, className }: StarRatingInputProps) {
  const variants = useStarsVariants()
  const [selectedStars, setSelectedStars] = useState(0)

  const selectRating = (rating: number) => {
    setSelectedStars(rating)
    onStarSelect?.(rating)
  }

  return (
    <div
      className={cn(variants.starsContainerVariants({ variant: 'interactive' }), className)}
      role="group"
      aria-label="Selecteaza rating"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          onClick={() => selectRating(i + 1)}
          role="button"
          tabIndex={0}
          aria-pressed={i < selectedStars}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              selectRating(i + 1)
            }
          }}
          aria-label={`Rate ${i + 1} stars`}
          className={cn(variants.starIconVariants({ interactive: true }), starSize)}
        >
          <StarIcon filled={i < selectedStars} className="h-full w-full" />
        </span>
      ))}
    </div>
  )
}
